// src/pages/AdminBlogsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiEdit2, FiPlus, FiTrash2, FiTag } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { BlogService } from '../services/blog.service';
import type { BlogPost } from '../types/Blog';
import styles from './AdminBlogsPage.module.css';

export default function AdminBlogsPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = () => {
    setLoading(true);
    BlogService.getAdminPosts()
      .then(res => setPosts(Array.isArray(res) ? res : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleDelete = async (id: string | number) => {
    const ok = window.confirm(t('admin.confirm_delete_blog') || '¿Eliminar publicación?');
    if (!ok) return;
    try {
      await BlogService.deletePost(id);
    } finally {
      fetchPosts();
    }
  };

  const rows = useMemo(() => {
    return posts.map(p => {
      const title = p.title?.trim() || '-';
      const statusLabel = p.is_active ? t('common.active') || 'Activo' : t('common.inactive') || 'Inactivo';
      const statusClass = p.is_active ? styles.statusActive : styles.statusInactive;

      return {
        id: p.id,
        title,
        statusLabel,
        statusClass
      };
    });
  }, [posts, t]);

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('common.blogs') || 'Blogs'}</h1>
          <p className={styles.subtitle}>{t('admin.blogs_subtitle') || 'Administra publicaciones y su visibilidad.'}</p>
        </div>

        <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Link to="/admin/blogs/categorias" className={styles.secondaryAction}>
            <FiTag className={styles.actionIcon} />
            {t('admin.categories') || 'Categorías'}
          </Link>

          <Link to="/admin/blogs/new" className={styles.primaryAction}>
            <FiPlus className={styles.actionIcon} />
            {t('common.new') || 'Nuevo'}
          </Link>
        </div>
      </header>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('admin.posts') || 'Publicaciones'}</div>
          <div className={styles.countBadge}>{loading ? '—' : String(posts.length)}</div>
        </div>

        <div className={styles.cardBody}>
          {loading ? (
            <div className={styles.loading}>{t('common.loading') || 'Cargando...'}</div>
          ) : rows.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{t('admin.no_results') || 'No hay publicaciones'}</div>
              <div className={styles.emptyHint}>{t('admin.create_first_blog_hint') || 'Crea tu primera publicación para empezar.'}</div>
              <Link to="/admin/blogs/new" className={styles.secondaryAction}>
                <FiPlus className={styles.actionIcon} />
                {t('common.new') || 'Nuevo'}
              </Link>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thTitle}>{t('admin.table_title') || 'Título'}</th>
                    <th className={styles.thStatus}>{t('admin.table_status') || 'Estado'}</th>
                    <th className={styles.thActions}>{t('admin.table_actions') || 'Acciones'}</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map(r => (
                    <tr key={String(r.id)} className={styles.tr}>
                      <td className={styles.tdTitle}>
                        <div className={styles.titleCell}>
                          <div className={styles.postTitle}>{r.title}</div>
                        </div>
                      </td>

                      <td className={styles.tdStatus}>
                        <span className={`${styles.statusChip} ${r.statusClass}`}>{r.statusLabel}</span>
                      </td>

                      <td className={styles.tdActions}>
                        <div className={styles.actions}>
                          <Link to={`/admin/blogs/${r.id}/edit`} className={styles.iconButton} aria-label={t('common.edit') || 'Editar'}>
                            <FiEdit2 />
                          </Link>

                          <button
                            type="button"
                            className={`${styles.iconButton} ${styles.danger}`}
                            onClick={() => handleDelete(r.id)}
                            aria-label={t('common.delete') || 'Eliminar'}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
